import type { AppState, CatalogueItem, InventoryEntry, Placement } from '../types'

const fish = (
  id: string,
  name: string,
  rarity: CatalogueItem['rarity'],
  shape: string,
  colors: CatalogueItem['colors'],
  collection: string,
  unlock: CatalogueItem['unlock'] = 'discovery',
): CatalogueItem => ({
  id,
  name,
  category: 'fish',
  rarity,
  shape,
  colors,
  collection,
  unlock,
  description: `${name} brings a calm, distinct swimming rhythm to your cove.`,
})

const item = (
  id: string,
  name: string,
  category: CatalogueItem['category'],
  rarity: CatalogueItem['rarity'],
  shape: string,
  colors: CatalogueItem['colors'],
  collection: string,
  unlock: CatalogueItem['unlock'] = 'discovery',
): CatalogueItem => ({ id, name, category, rarity, shape, colors, collection, unlock, description: `${name} adds a quiet detail to your living cove.` })

export const catalogue: CatalogueItem[] = [
  fish('fish-guppy', 'Sunset Guppy', 'common', 'flowing', ['#ffd477', '#f18c72', '#fff4ca'], 'River Garden', 'starter'),
  fish('fish-platy', 'Coral Platy', 'common', 'round', ['#fa8f70', '#ffe0a3', '#fff5dc'], 'River Garden', 'starter'),
  fish('fish-neon', 'Neon Tetra', 'common', 'slim', ['#2dd6cb', '#1b6688', '#ef5d6f'], 'Moonlit Cove', 'starter'),
  fish('fish-danio', 'Zebra Danio', 'common', 'slim', ['#c7e7df', '#476f79', '#f2d08a'], 'River Garden'),
  fish('fish-cory', 'Pepper Cory', 'uncommon', 'bottom', ['#b7aa8e', '#4d5f5c', '#f0dfbd'], 'Tiny Ruins'),
  fish('fish-gourami', 'Dwarf Gourami', 'uncommon', 'disc', ['#e67b65', '#4aa9ae', '#ffe2af'], 'Coral Nursery'),
  fish('fish-betta', 'Velvet Betta', 'rare', 'betta', ['#7b5dc7', '#2a7291', '#e8d9ff'], 'Moonlit Cove'),
  fish('fish-angel', 'Silver Angelfish', 'rare', 'triangle', ['#e5eff0', '#536d78', '#ffd89a'], 'Moonlit Cove'),
  fish('fish-gold', 'Honey Goldfish', 'uncommon', 'round', ['#f8b24f', '#e86d4b', '#fff1c0'], 'Coral Nursery'),
  fish('fish-clown', 'Cove Clownfish', 'special', 'round', ['#f28454', '#fff4df', '#293e46'], 'Coral Nursery'),

  item('plant-anubias', 'Broad-leaf Anubias', 'plant', 'common', 'broad', ['#2d8d67', '#56b982', '#b8e6bc'], 'River Garden', 'starter'),
  item('plant-fern', 'Java Fern', 'plant', 'common', 'fern', ['#2b9369', '#70c68d', '#d0eab9'], 'River Garden'),
  item('plant-vallisneria', 'Vallisneria Grass', 'plant', 'common', 'grass', ['#2d7e69', '#58ad79', '#a4d793'], 'River Garden'),
  item('plant-moss', 'Soft Moss Mound', 'plant', 'uncommon', 'moss', ['#4e8f5f', '#80b86f', '#c7d989'], 'Tiny Ruins'),
  item('plant-lotus', 'Water Lotus', 'plant', 'rare', 'lotus', ['#4b9f7e', '#e394a9', '#f6d6de'], 'Moonlit Cove'),
  item('plant-coral', 'Quiet Fan Coral', 'plant', 'uncommon', 'coral', ['#d87879', '#f2a184', '#ffd0b1'], 'Coral Nursery'),

  item('decor-pebbles', 'Sea-glass Pebbles', 'decor', 'common', 'pebbles', ['#7bb6ae', '#aacfc3', '#e0d09c'], 'River Garden', 'starter'),
  item('decor-rock', 'Smooth Rock Shelter', 'decor', 'common', 'rock', ['#668884', '#94aaa0', '#c6c1a4'], 'River Garden', 'starter'),
  item('decor-arch', 'Stacked Stone Arch', 'decor', 'uncommon', 'arch', ['#71867f', '#9ca89b', '#d1c5a8'], 'Tiny Ruins'),
  item('decor-driftwood', 'Driftwood Branch', 'decor', 'common', 'wood', ['#8b684d', '#b88c67', '#d7b184'], 'River Garden'),
  item('decor-log', 'Hollow Root Tunnel', 'decor', 'uncommon', 'tunnel', ['#7b5e47', '#ad8058', '#d3a674'], 'River Garden'),
  item('decor-shells', 'Hero Shell', 'decor', 'common', 'shell', ['#edc9a5', '#fff1d6', '#d78e7b'], 'Coral Nursery'),
  item('decor-sand', 'Warm Sand Mound', 'decor', 'common', 'sand', ['#d7bd83', '#f1d8a0', '#fff0c7'], 'River Garden'),
  item('decor-ruin', 'Little Ruin', 'decor', 'rare', 'ruin', ['#6f8984', '#a5b4a4', '#d9cfae'], 'Tiny Ruins'),
  item('decor-lighthouse', 'Tiny Lighthouse', 'decor', 'rare', 'lighthouse', ['#f2eee1', '#e87966', '#ffd984'], 'Moonlit Cove'),
  item('decor-house', 'Ceramic Cove House', 'decor', 'uncommon', 'house', ['#63a7a3', '#e6b878', '#fff1ca'], 'River Garden'),
  item('decor-journal', 'Sunken Journal', 'decor', 'rare', 'book', ['#715741', '#b98756', '#e4c18a'], 'Tiny Ruins'),
  item('decor-compass', 'Compass Medallion', 'decor', 'rare', 'compass', ['#c79a54', '#f0d490', '#6b7f7d'], 'Tiny Ruins'),
  item('decor-moongate', 'Moon Gate', 'decor', 'special', 'moongate', ['#7a84aa', '#afb2d2', '#e8ddff'], 'Moonlit Cove'),
  item('decor-chest', 'Keepsake Chest', 'decor', 'rare', 'chest', ['#896144', '#d19b5d', '#f1d39a'], 'Tiny Ruins'),
  item('decor-lantern', 'Cove Lantern', 'decor', 'special', 'lantern', ['#396f74', '#ffd77d', '#fff0bd'], 'Moonlit Cove'),

  item('bg-seaglass', 'Sea Glass Day', 'background', 'common', 'seaglass', ['#42b7ae', '#0f6571', '#062f3b'], 'River Garden', 'starter'),
  item('bg-deep', 'Deep Cove Evening', 'background', 'rare', 'deep', ['#15506a', '#102f48', '#071b2b'], 'Moonlit Cove'),
  item('bg-river', 'River Garden', 'background', 'uncommon', 'river', ['#51a79b', '#2c766e', '#164849'], 'River Garden'),
]

export const byId = new Map(catalogue.map((entry) => [entry.id, entry]))

const starterInventory: InventoryEntry[] = [
  { itemId: 'plant-anubias', source: 'starter', obtainedAt: 0 },
  { itemId: 'decor-pebbles', source: 'starter', obtainedAt: 0 },
  { itemId: 'decor-rock', source: 'starter', obtainedAt: 0 },
  { itemId: 'bg-seaglass', source: 'starter', obtainedAt: 0 },
]

const starterPlacements: Placement[] = [
  { itemId: 'plant-anubias', x: 18, y: 81, scale: 1, layer: 'mid' },
  { itemId: 'decor-rock', x: 80, y: 83, scale: 1, layer: 'mid' },
  { itemId: 'decor-pebbles', x: 51, y: 90, scale: 0.8, layer: 'front' },
]

export const initialState: AppState = {
  version: 1,
  onboardingComplete: false,
  starterChosen: false,
  feed: 0,
  discoveryMarks: 0,
  tickets: 0,
  guarantee: 0,
  spins: 0,
  inventory: starterInventory,
  fish: [],
  placements: starterPlacements,
  activeBackground: 'bg-seaglass',
  sessions: [],
  pendingTaskLogs: [],
  settings: {
    reducedMotion: false,
    staticFocus: false,
    quality: 'balanced',
    sound: false,
    notifications: false,
    highContrast: false,
  },
}

export const starterFish = catalogue.filter((entry) => entry.category === 'fish' && entry.unlock === 'starter')
